import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type UsageMetersResult } from '@whinerp/client';
import { Num, Skeleton } from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';

/**
 * Usage bars (F-143).
 *
 * ONE component, on the plan page and in Setup home, so the two cannot come to
 * disagree about how full a meter is.
 *
 * A meter with no ceiling shows the number and says so, rather than drawing a
 * full bar or an empty one - both of which are claims about a limit that does
 * not exist.
 */
export function UsageBars({ compact = false }: { compact?: boolean }): JSX.Element {
  const t = useT();
  const format = useFormat();

  const usage = useQuery<UsageMetersResult, ApiError>({
    queryKey: ['usage'],
    queryFn: () => api.usageMeters({}),
  });

  if (usage.isPending) return <Skeleton className="h-block-sm w-full" />;
  // Not a skeleton: one rendered on error never resolves, and the screen waits
  // for it forever.
  if (usage.isError) return <></>;

  return (
    <ul data-usage-bars className="flex flex-col gap-2">
      {usage.data.meters.map((meter) => (
        <li key={meter.key} data-usage-meter={meter.key} data-usage-state={meter.state} className="flex flex-col gap-1">
          <div className="flex flex-wrap items-baseline gap-2 text-dense text-ink">
            <span>{meter.label}</span>
            <Num className="text-ink-600">
              {meter.limit === null
                ? t('usage.unlimited', { values: { used: formatCount(meter.used, format) } })
                : t('usage.of', {
                    values: {
                      used: formatCount(meter.used, format),
                      limit: formatCount(meter.limit, format),
                    },
                  })}
            </Num>
            {meter.perPeriod ? <span className="text-meta text-ink-meta">{t('usage.period')}</span> : null}
          </div>

          {/*
            No bar without a ceiling. A full bar would say "you are at your
            limit" and an empty one "you have used nothing", and neither is
            true of a plan with no limit at all.
          */}
          {meter.percent === null ? null : (
            <div
              className="h-chip w-full overflow-hidden rounded-chip bg-sunken"
              role="progressbar"
              aria-label={meter.label}
              /*
               * Clamped for the bar, exact for the reader.
               *
               * A meter can be over its limit - 200% of three seats is a real
               * state - and `aria-valuenow` above `aria-valuemax` is invalid,
               * so a screen reader would announce something arbitrary.
               * `aria-valuetext` carries the true figure, which is the one a
               * person needs.
               */
              aria-valuenow={Math.min(100, meter.percent)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuetext={`${String(meter.percent)}%`}
            >
              <div
                data-usage-fill={String(meter.percent)}
                className={
                  meter.state === 'exceeded' || meter.state === 'critical'
                    ? 'h-full bg-danger'
                    : meter.state === 'warning'
                      ? 'h-full bg-warning'
                      : 'h-full bg-bronze'
                }
                /*
                 * The width is the number, stopping at full.
                 *
                 * A bar cannot be more than full, so above the limit the bar
                 * and the figure beside it part company - which is the honest
                 * way round: the figure says 200%, the bar says "past the
                 * end", and `data-usage-fill` carries the exact number so
                 * nothing has to infer it from a width.
                 */
                style={{ width: `${String(Math.min(100, meter.percent))}%` }}
              />
            </div>
          )}
        </li>
      ))}
      {compact ? null : null}
    </ul>
  );
}
